import { useState } from 'react';
import { RunButtons } from '@/features/runs/RunButtons';
import { DeskButton } from '@/features/runs/DeskButton';
import type { RunRequest, RunSubject } from '@/api/runRequests';
import { isOpen } from '@/features/runs/runState';
import { Link } from 'react-router-dom';
import { Copy, ExternalLink, Sparkles } from 'lucide-react';
import { Disclosure, NoteDisclosure } from '@/components/Disclosure';
import { StatusPill } from '@/components/Status';
import type { TodayFact, TodayItem } from '@/api/today';
import { useMailPreviewStore } from '@/features/mail-preview/mailPreviewStore';
import { cn } from '@/lib/utils';
import { AskActions } from './AskActions';
import { KIND_LABEL, chipTone, isLate, refTarget, titleParts, todayOrderAnchor } from './todayLib';

function FactValue({ fact, item }: { fact: TodayFact; item: TodayItem }) {
  const openPreview = useMailPreviewStore((s) => s.openPreview);
  const target = refTarget(fact.ref, item);
  const linkClass = 'text-primary hover:underline';
  if (!target) return <>{fact.value}</>;
  if (target.kind === 'route') return <Link to={target.to} className={linkClass}>{fact.value}</Link>;
  if (target.kind === 'external') {
    return <a href={target.href} target="_blank" rel="noreferrer" className={linkClass}>{fact.value}</a>;
  }
  return (
    <button type="button" className={cn(linkClass, 'text-left')}
      onClick={() => openPreview(target.messageId, { journeyId: item.journey?.id ?? null, label: fact.label })}>
      {fact.value}
    </button>
  );
}

function OpenLink({ open }: { open: NonNullable<TodayItem['open']> }) {
  const cls = 'inline-flex items-center gap-1 px-1 text-xs font-medium text-primary hover:underline';
  if (open.href.startsWith('/')) return <Link to={open.href} className={cls}>{open.label}</Link>;
  return <a href={open.href} target="_blank" rel="noreferrer" className={cls}>{open.label}<ExternalLink className="h-3 w-3" /></a>;
}

function ItemActions({ item, onChanged, onSessionChange }: {
  item: TodayItem;
  onChanged: () => void;
  onSessionChange: (request: RunRequest | null) => void;
}) {
  const subject = runSubject(item);
  return (
    <AskActions item={item} onChanged={onChanged}>
      {item.open && <OpenLink open={item.open} />}
      {subject && <span className="ml-auto"><RunButtons subject={subject} checkOnMount={false} runNow={false}
        session={item.session} onSessionChange={onSessionChange} /></span>}
      {item.source === 'desk' && <span className="ml-auto"><DeskButton initial={item.desk?.request ?? null} /></span>}
    </AskActions>
  );
}

/** What Work this with Claude acts on: the ask itself, else its setup folder, else its journey. */
function runSubject(item: TodayItem): RunSubject | null {
  if (item.ask?.id) return { actionId: item.ask.id };
  if (item.setup_folder) return { setupFolder: item.setup_folder };
  if (item.journey?.id) return { orderId: item.journey.id };
  return null;
}

export function Errand({ errand }: { errand: NonNullable<TodayItem['errand']> }) {
  const [copied, setCopied] = useState<number | null>(null);
  return (
    <ol className="grid list-decimal gap-1 pl-5 text-sm marker:text-muted-foreground">
      {errand.steps.map((s, i) => (
        <li key={i} className="pl-1">
          {s.link ? <a href={s.link} target="_blank" rel="noreferrer" className="text-primary hover:underline">{s.text}</a> : s.text}
          {s.copy && (
            <button type="button" className="ml-2 inline-flex items-center gap-1 rounded bg-muted px-1.5 font-mono text-xs hover:bg-accent"
              onClick={() => { void navigator.clipboard?.writeText(s.copy!).then(() => setCopied(i)).catch(() => undefined); }}>
              {s.copy}<Copy className="h-3 w-3" />{copied === i && <span className="font-sans text-success-ink">copied</span>}
            </button>
          )}
        </li>
      ))}
    </ol>
  );
}

function Facts({ item }: { item: TodayItem }) {
  if (!item.facts.length) return null;
  return (
    <dl className="grid grid-cols-[repeat(auto-fill,minmax(11.5rem,1fr))] gap-x-5 gap-y-1.5">
      {item.facts.map((f, i) => (
        <div key={i} className="min-w-0">
          <dt className="text-xs text-muted-foreground">{f.label}</dt>
          <dd className="text-sm tabular-nums wrap-anywhere"><FactValue fact={f} item={item} /></dd>
        </div>
      ))}
    </dl>
  );
}

/** One Today item: what it is, why now, the numbers that decide it, and the buttons that do it. */
export function TodayItemCard({ item, related = [], onChanged, nested = false, highlighted = false }: {
  item: TodayItem;
  related?: TodayItem[];
  onChanged: () => void;
  nested?: boolean;
  /** Arrived at from a link (`/today#order-<journey id>`). */
  highlighted?: boolean;
}) {
  const t = titleParts(item);
  const journey = item.journey;
  // The card's own session: Today's read, then whatever its button learns (a tap, a poll).
  const [session, setSession] = useState<RunRequest | null>(item.session ?? null);
  const withClaude = isOpen(session);
  const relatedWithClaude = related.filter((r) => isOpen(r.session ?? null)).length;
  return (
    <article id={!nested && item.source === 'order' && journey ? todayOrderAnchor(journey.id) : undefined} className={cn(
      'grid scroll-mt-4 gap-2.5',
      nested ? 'rounded-md bg-muted/40 p-3' : 'rounded-[10px] border border-border bg-card px-4 py-3.5 shadow-card max-md:px-3.5',
      !nested && isLate(item) && 'shadow-[inset_3px_0_0_var(--tone-danger),var(--shadow-card)]',
      highlighted && 'ring-2 ring-primary',
    )}>
      <header className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1.5">
        <div className="min-w-0 flex-[1_1_22rem]">
          {!nested && <div className="text-[11px] font-semibold uppercase tracking-[.08em] text-muted-foreground">{KIND_LABEL[item.source]}</div>}
          {t.vendor && journey && (
            <Link to={`/journeys/${journey.id}`} className="text-sm hover:underline">
              <span className="font-semibold">{t.vendor}</span>
              {(journey.label ?? t.release) && <span className="text-muted-foreground"> · {journey.label ?? t.release}</span>}
            </Link>
          )}
          <div className={cn('text-[15px] font-medium leading-snug', nested && 'text-sm')}>
            {t.ask}
          </div>
          {!t.vendor && journey?.vendor && (
            <Link to={`/journeys/${journey.id}`} className="text-xs text-muted-foreground hover:underline">
              {journey.vendor}{journey.label ? ` · ${journey.label}` : ''}
            </Link>
          )}
        </div>
        {(item.why.length > 0 || withClaude) && (
          <div className="flex flex-wrap gap-1.5">
            {withClaude && (
              <StatusPill tone="info" icon={<Sparkles className="h-3 w-3" />}>
                {session!.claimedAt ? 'Claude session open' : 'Claude session starting'}
              </StatusPill>
            )}
            {item.why.map((c, i) => <StatusPill key={i} tone={chipTone(c)} className="tabular-nums">{c.label}</StatusPill>)}
          </div>
        )}
      </header>
      <Facts item={item} />
      {item.errand && <Errand errand={item.errand} />}
      <NoteDisclosure text={item.detail} label="Why" />
      <ItemActions item={item} onChanged={onChanged} onSessionChange={setSession} />
      {related.length > 0 && (
        <Disclosure label={<>{related.length} more on this order{relatedWithClaude > 0 && (
          <span className="text-info-ink">· {relatedWithClaude} with Claude</span>
        )}</>} className="border-t border-border pt-2" contentClassName="grid gap-2">
          {related.map((r) => <TodayItemCard key={r.key} item={r} onChanged={onChanged} nested />)}
        </Disclosure>
      )}
    </article>
  );
}
